import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const WebDesignPage: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile-First Design',
      description: 'Responsive websites that look perfect on all devices, ensuring you never lose mobile customers.'
    },
    {
      icon: 'ri-speed-up-line',
      title: 'Fast Loading Speed',
      description: 'Optimized websites that load in under 3 seconds, reducing bounce rates and improving SEO.'
    },
    {
      icon: 'ri-search-line',
      title: 'SEO-Ready Structure',
      description: 'Built with clean code and SEO best practices to help you rank higher in search results.'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'Conversion Focused',
      description: 'Strategic design elements that guide visitors toward taking action and becoming customers.'
    }
  ];

  const features = [
    'Custom responsive design',
    'Content management system',
    'SSL certificate included',
    'Google Analytics setup',
    'Contact forms integration',
    'Social media integration',
    'Basic SEO optimization',
    '30 days free support'
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-pink-100 to-purple-100 rounded-full px-6 py-2 mb-6">
            <i className="ri-palette-line text-pink-600 mr-2"></i>
            <span className="text-pink-800 font-medium">Professional Web Design</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              Beautiful Websites
            </span>
            <br />
            That Convert Visitors
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Transform your online presence with custom-designed websites that not only look stunning but also drive results for your small business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-white text-purple-600 border-2 border-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-purple-50 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                Why Small Businesses Choose Our Web Design
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique challenges small businesses face online. Our websites are designed to help you compete with larger companies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                  Everything You Need Included
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our web design packages include all the essential features your small business needs to succeed online, with no hidden costs.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/14ee44a1593f336854550708afa42982.jpg"
                alt="Web Design Showcase"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Online Presence?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's create a website that showcases your business and drives real results.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/consultation"
              className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-white hover:text-pink-200 transition-colors duration-300">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-pink-200 transition-colors duration-300">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-pink-200 transition-colors duration-300">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-pink-200 transition-colors duration-300">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default WebDesignPage;
